import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Input } from "antd";
import { notification } from "antd";
import moment from "moment-timezone";

import DateFiltersPicker from "../../common/DateFiltersPicker";
import FilterBoxCard from "../../common/Card/FilterBoxCard";
import { getBerlinMomentTimezone } from "../../common/helpers";
import { centsToEuros } from "../../../utils/amount";
import { Transaction } from "../../../types/transactions";
import api from "../../../api";
import { SAVE_DATE_FORMAT } from "@/v1/constants";

const BERLIN_TIMEZONE = "Europe/Berlin";

/**
 * Sent on every page request, as the Verification View does. Only with an
 * explicit sort does the backend add `id ASC` as a tie-breaker; its default
 * `ORDER BY valuta_date DESC` leaves rows sharing a value date in no fixed
 * order, so with offset pages they could appear twice or never.
 */
export const TRANSACTION_PICKER_SORT = {
  field: "valuta_date",
  direction: "DESC",
};

/**
 * `valuta_date` is stored as a timestamp, but the date picker only yields
 * calendar days (SAVE_DATE_FORMAT). Convert the picker's range into the whole
 * Berlin day, start to end, so a transaction that lands on a boundary day is
 * not lost to a naive UTC-midnight comparison.
 */
const toBerlinDayRange = (
  dateFrom: string,
  dateTo: string
): { dateFrom: string; dateTo: string } => ({
  dateFrom: moment
    .tz(dateFrom, SAVE_DATE_FORMAT, BERLIN_TIMEZONE)
    .startOf("day")
    .toISOString(),
  dateTo: moment
    .tz(dateTo, SAVE_DATE_FORMAT, BERLIN_TIMEZONE)
    .endOf("day")
    .toISOString(),
});

const { Search } = Input;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
const SearchWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;
const HeaderText = styled.div`
  font-size: 20px;
`;

const DateFiltersWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 200px;
`;

const defaultFilterOptions = {
  dateFrom: undefined,
  dateTo: undefined,
};

/**
 * Format an OCR amount (in cents) into a search term, e.g. 995 -> "9,95".
 * Uses the absolute value because the amount search already matches both signs
 * (amount_in: [x, -x]), and produces no thousands separator so the value stays
 * valid for the amount search regex (/^-?\d+([,.]\d{1,2})?$/).
 */
const formatAmountSearchTerm = (amountInCents: number): string =>
  centsToEuros(Math.abs(amountInCents)).toFixed(2).replace(".", ",");

/*
 * `customerEmail` and `receiptId` describe the receipt currently on screen and
 * are passed down by the view that resolved it. Neither may be read back from
 * the URL, for two separate reasons:
 *
 * - The `email` param is written after the receipt renders, so it still names
 *   the previous customer while the new receipt is displayed (SMT-1320).
 * - In the Receipt overview that param holds whatever the operator typed into
 *   the customer search, often a public id. The transactions query resolves the
 *   customer by exact email only, so a public id makes the backend answer 404.
 *   The transactions field is non-nullable, so the whole response loses its
 *   data and reaches the client as a 500, which the Sentry filter then drops
 *   because it reports from 500 upwards (SMT-1283).
 */
const TransactionSearch = ({
  onSearch,
  customerEmail,
  receiptId,
  prefillAmount,
  page,
  pageSize,
  onResetPage,
}: {
  onSearch: (transactions: Transaction[], total: number) => void;
  customerEmail?: string;
  receiptId?: string;
  prefillAmount?: number;
  page: number;
  pageSize: number;
  /** Called whenever a NEW search or a date change starts, so the parent can go back to page 1. */
  onResetPage: () => void;
}) => {
  const [searchInput, setSearchInput] = useState("");
  // The SUBMITTED search: the text, the customer it belongs to, and a `nonce`.
  // `null` means no search has been requested for this receipt yet (distinct
  // from "" which searches everything). The `nonce` makes re-submitting the
  // same text still trigger a refetch (AC 4), since two equal strings would
  // otherwise not change as an effect dep.
  //
  // `customerEmail` is carried IN here rather than read from the prop by the
  // fetch effect, so that text and customer can never disagree: a receipt
  // switch changes both in a single state update, and the fetch effect
  // therefore fires exactly once, with both values from the same receipt. Read
  // from the prop instead, the effect would run once in the same commit as the
  // receipt-change effect below - with the NEW customer and the PREVIOUS
  // receipt's search term (SMT-1320).
  const [query, setQuery] = useState<{
    text: string;
    nonce: number;
    customerEmail: string | undefined;
  } | null>(null);
  const [searchDates, setSearchDates] = useState<{
    dateFrom: string | undefined;
    dateTo: string | undefined;
  }>(defaultFilterOptions);
  const [isLoading, setIsLoading] = useState(false);
  // Identifies the most recent search so that stale responses can be ignored.
  const latestRequestId = useRef(0);

  useEffect(() => {
    setSearchDates(defaultFilterOptions);
    onResetPage();

    // When the opened receipt has an OCR-read amount, pre-fill the search with
    // it and run it once so matching candidates show up immediately. Otherwise
    // keep the previous behaviour of starting from an empty search.
    if (prefillAmount != null) {
      const amountTerm = formatAmountSearchTerm(prefillAmount);
      setSearchInput(amountTerm);
      setQuery({ text: amountTerm, nonce: Date.now(), customerEmail });
    } else {
      setSearchInput("");
      setQuery(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiptId, customerEmail, prefillAmount]);

  useEffect(() => {
    /*
     * Claim the request id before the guards below: a search already in flight
     * must be invalidated even when this effect run cannot fetch, otherwise its
     * response is still the newest one and lands on the receipt that has since
     * replaced it, with the previous customer's candidates (SMT-1320).
     */
    const requestId = ++latestRequestId.current;

    /*
     * `query === null` means the receipt-change effect above has not yet
     * requested a search (no OCR amount, operator hasn't searched). Leave the
     * parent's own display alone in this case: it is still showing the
     * receipt's match candidates (the `transactions` prop), and this effect
     * can re-run with no search requested on a plain page click. The request
     * id was already claimed above, so a search still in flight for the
     * previous receipt is invalidated either way.
     */
    if (query === null) {
      setIsLoading(false);
      return;
    }

    /*
     * The customer comes from the receipt the search was submitted for, so
     * there is also nothing to search for until we have one; guarding here
     * keeps the request from going out without its required email, which the
     * backend rejects with a 400.
     */
    if (!query.customerEmail) {
      onSearch([], 0);
      setIsLoading(false);
      return;
    }

    const fetchPage = async () => {
      setIsLoading(true);
      try {
        const { transactions, count } = await api.kontax.fetchTransactions(
          {
            email: query.customerEmail,
            ...(query.text ? { search: query.text } : {}),
            ...(searchDates.dateFrom && searchDates.dateTo
              ? toBerlinDayRange(searchDates.dateFrom, searchDates.dateTo)
              : {}),
            includeAll: true,
            sort: TRANSACTION_PICKER_SORT,
          },
          undefined,
          { page, pageSize }
        );

        // The search auto-fires on every receipt/date/page change, so requests
        // can overlap. Apply only the newest one's response to avoid an older
        // result overwriting the current receipt's candidate list.
        if (requestId !== latestRequestId.current) {
          return;
        }
        onSearch(transactions, count ?? transactions.length);
        setIsLoading(false);
      } catch (err) {
        if (requestId !== latestRequestId.current) {
          return;
        }
        notification.error({
          message: `An error occurred: ${(err as Error).message}`,
        });
        setIsLoading(false);
      }
    };
    fetchPage();
    /*
     * `customerEmail` is deliberately NOT a dependency: the customer this
     * effect uses lives in `query`, and only the receipt-change effect above
     * changes it - together with the search text, in one state update. Adding
     * the prop back would make a receipt switch fire this effect twice, the
     * first time with the new customer and the previous receipt's search term.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, searchDates, page, pageSize]);

  const onDateChangeHandler = (dateOptions: {
    dateFrom: string;
    dateTo: string;
  }) => {
    onResetPage();
    setSearchDates(dateOptions);
    // A date change with empty input searches everything in the range, as today.
    setQuery({ text: searchInput, nonce: Date.now(), customerEmail });
  };

  return (
    <Wrapper>
      <SearchWrapper>
        <HeaderText>Choose a transaction</HeaderText>
        <Search
          loading={isLoading}
          value={searchInput}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setSearchInput(event.target.value);
          }}
          onSearch={(value) => {
            onResetPage();
            setQuery({ text: value, nonce: Date.now(), customerEmail });
          }}
          id="transaction-search"
          placeholder="Search amount, name or description"
        />
      </SearchWrapper>
      <FilterBoxCard title="Transaction date">
        <DateFiltersWrapper>
          <DateFiltersPicker
            dateFrom={searchDates.dateFrom}
            dateTo={searchDates.dateTo}
            maxDate={getBerlinMomentTimezone().format()}
            onChange={onDateChangeHandler}
          />
        </DateFiltersWrapper>
      </FilterBoxCard>
    </Wrapper>
  );
};

export default TransactionSearch;
